'use strict'

// A page layout column can take a fixed share of its row, or a fixed pixel
// width. An empty width shares whatever space the sized columns leave.

const PERCENT = 'percent'
const PIXEL = 'pixel'
const OVERFLOW_SCROLL = 'scroll'
const OVERFLOW_MOBILE = 'mobile'
const MAX_PERCENT = 100
const MAX_PIXELS = 4000
const FIT_SLOP = 8
const DESIGNER_COLUMN_MIN_WIDTH = 280

function blankWidth (value) {

  if (value == null || value === '') return true

  if (typeof value === 'object') {

    return value.width == null || String(value.width).trim() === ''
  }

  return false
}

function unitName (unit) {

  const text = String(unit || '').trim().toLowerCase()

  if (text === PERCENT || text === 'percentage' || text === '%') return PERCENT

  if (text === PIXEL || text === 'pixels' || text === 'px') return PIXEL

  return ''
}

function roundedWidth (number, unit) {

  if (!Number.isFinite(number) || number <= 0) return null

  const rounded = unit === PIXEL
    ? Math.round(number)
    : Math.round(number * 100) / 100

  if (rounded <= 0) return null

  if (unit === PERCENT && rounded > MAX_PERCENT) return null

  if (unit === PIXEL && rounded > MAX_PIXELS) return null

  return rounded
}

function readWidth (width, unit) {

  const text = String(width == null ? '' : width).trim()
  const matched = text.match(/^(\d+(?:\.\d+)?)\s*(%|px)?$/i)

  if (!matched) return null

  const resolved = matched[2] ? unitName(matched[2]) : unitName(unit)

  if (!resolved) return null

  const number = roundedWidth(Number(matched[1]), resolved)

  if (number == null) return null

  return { width: number, unit: resolved }
}

// { width, unit }, "40%", "240px", or blank. Blank and unusable values are null.
function normalizeColumnFormat (value) {

  if (blankWidth(value)) return null

  if (typeof value === 'string' || typeof value === 'number') return readWidth(value, '')

  if (typeof value === 'object') return readWidth(value.width, value.unit || value.widthUnit)

  return null
}

function columnFormatProblem (value) {

  if (blankWidth(value)) return ''

  if (normalizeColumnFormat(value)) return ''

  return 'Enter a width greater than 0. A percentage can be at most 100. A pixel width can be at most 4000.'
}

function overflowName (value) {

  const text = String(value || '').trim().toLowerCase()

  if (text === OVERFLOW_MOBILE) return OVERFLOW_MOBILE

  if (text === OVERFLOW_SCROLL || text === '') return OVERFLOW_SCROLL

  return ''
}

// Width plus the optional overflow choice. Scroll is the default and is not stored.
function columnFormatRecord (value) {

  if (blankWidth(value) && (!value || typeof value !== 'object' || !value.overflow)) return null

  const width = normalizeColumnFormat(value)
  const overflow = value && typeof value === 'object' ? overflowName(value.overflow) : OVERFLOW_SCROLL

  if (value && typeof value === 'object' && value.overflow && !overflow) return width

  if (!width && overflow !== OVERFLOW_MOBILE) return null

  const record = {}

  if (width) {

    record.width = width.width
    record.unit = width.unit
  }

  if (overflow === OVERFLOW_MOBILE) record.overflow = OVERFLOW_MOBILE

  return record
}

function columnFormatLabel (value) {

  const record = columnFormatRecord(value)

  if (!record) return ''

  const width = record.unit === PERCENT
    ? `${record.width}%`
    : (record.unit === PIXEL ? `${record.width}px` : '')

  if (record.overflow === OVERFLOW_MOBILE && width) return `${width} · mobile`

  if (record.overflow === OVERFLOW_MOBILE) return 'mobile'

  return width
}

// While the mobile layout is on, stay there until the column is wide enough
// for the width the normal layout needed. Measuring again would see the
// smaller mobile layout and flip back.
function columnFitDecision ({ overflow, available, needed, mobile, recordedNeed }) {

  if (overflow !== OVERFLOW_MOBILE) return { mobile: false, recordedNeed: 0 }

  const width = Number(available) || 0
  const content = Number(needed) || 0
  const remembered = Number(recordedNeed) || 0

  if (mobile) {

    if (remembered > 0 && width + FIT_SLOP >= remembered) return { mobile: false, recordedNeed: 0 }

    return { mobile: true, recordedNeed: remembered }
  }

  if (content > width + FIT_SLOP) return { mobile: true, recordedNeed: content }

  return { mobile: false, recordedNeed: 0 }
}

function columnFitNeeded ({ narrow, recordedNeed, contentWidth }) {

  if (narrow) return Number(recordedNeed) || 0

  return Number(contentWidth) || 0
}

function columnContentWidth (element) {

  if (!element || typeof element.querySelectorAll !== 'function') return 0

  let needed = 0

  element.querySelectorAll('table, .pivot-scroll, .cards-grid, .calendar-grid').forEach((node) => {

    needed = Math.max(needed, node.scrollWidth || 0)
  })

  return needed
}

// Fixed columns do not grow. A column with no width keeps the row's equal share.
function columnFormatStyle (value) {

  const format = normalizeColumnFormat(value)

  if (!format) return null

  const size = format.unit === PERCENT ? `${format.width}%` : `${format.width}px`

  return {
    flex: `0 0 ${size}`,
    width: size,
    maxWidth: size,
    minWidth: format.unit === PERCENT ? '0' : size
  }
}

// The designer draws the same share of the row, but the column chrome
// (title, width, and buttons) needs a floor so a small percentage cannot
// pile those controls on top of each other.
function columnDesignerStyle (value) {

  const style = columnFormatStyle(value)

  if (!style) return null

  return Object.assign({}, style, { minWidth: `${DESIGNER_COLUMN_MIN_WIDTH}px` })
}

function assignColumnFormat (target, component) {

  if (!target || !component || component.type !== 'column') return

  const format = columnFormatRecord(component.columnFormat)

  if (format) target.columnFormat = format
}

// Layout tools send width and unit, or a columnFormat object.
function layoutOverflow (column) {

  if (!column || typeof column !== 'object') return ''

  if (column.columnFormat && typeof column.columnFormat === 'object' && column.columnFormat.overflow) {

    return overflowName(column.columnFormat.overflow)
  }

  return overflowName(column.overflow)
}

function columnFormatFromLayout (column) {

  if (!column || typeof column !== 'object') return { format: null, specified: false, widthIgnored: false }

  const overflow = layoutOverflow(column)
  const overflowIgnored = !!(
    (column.columnFormat && typeof column.columnFormat === 'object' && column.columnFormat.overflow && !overflow)
    || (column.overflow && !overflowName(column.overflow))
  )

  if (column.columnFormat != null && column.columnFormat !== '') {

    return {
      format: columnFormatRecord(column.columnFormat),
      specified: !blankWidth(column.columnFormat) || overflow === OVERFLOW_MOBILE,
      widthIgnored: !blankWidth(column.columnFormat) && !normalizeColumnFormat(column.columnFormat),
      overflowIgnored
    }
  }

  const hasWidth = Object.prototype.hasOwnProperty.call(column, 'width') && !blankWidth({ width: column.width })

  if (!hasWidth && overflow !== OVERFLOW_MOBILE) {

    return { format: null, specified: false, widthIgnored: false, overflowIgnored }
  }

  return {
    format: columnFormatRecord({
      width: hasWidth ? column.width : '',
      unit: column.unit || PERCENT,
      overflow: overflow === OVERFLOW_MOBILE ? OVERFLOW_MOBILE : OVERFLOW_SCROLL
    }),
    specified: true,
    widthIgnored: hasWidth && !normalizeColumnFormat({ width: column.width, unit: column.unit || PERCENT }),
    overflowIgnored
  }
}

module.exports = {
  DESIGNER_COLUMN_MIN_WIDTH,
  FIT_SLOP,
  MAX_PERCENT,
  MAX_PIXELS,
  OVERFLOW_MOBILE,
  OVERFLOW_SCROLL,
  PERCENT,
  PIXEL,
  assignColumnFormat,
  columnContentWidth,
  columnDesignerStyle,
  columnFitDecision,
  columnFitNeeded,
  columnFormatFromLayout,
  columnFormatLabel,
  columnFormatProblem,
  columnFormatRecord,
  columnFormatStyle,
  normalizeColumnFormat
}
